import type { ReactNode } from "react";
import { formatCents } from "@/lib/utils";
import { ExternalLink } from "lucide-react";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import type {
    MonthlyReceivables,
    MonthlyPayments,
    MonthlyRevenue,
} from "@/api/generated/client/models";

// ── Helpers ──────────────────────────────────────────

function fmtCents(cents: number): string {
    if (cents === 0) return "—";
    return formatCents(cents);
}

// ── Shared sub-components ────────────────────────────

function DataRow<T>({
    label,
    months,
    getValue,
    bold = false,
    color,
}: {
    label: string;
    months: T[];
    getValue: (m: T) => number;
    bold?: boolean;
    color?: "red" | "green" | "orange" | "blue" | "emerald";
}) {
    const colorClasses: Record<string, string> = {
        red: "text-red-500",
        green: "text-green-600 dark:text-green-400",
        orange: "text-orange-500",
        blue: "text-blue-600 dark:text-blue-400",
        emerald: "text-emerald-600 dark:text-emerald-400",
    };

    const colorClass = color ? colorClasses[color] : "";
    const weightClass = bold ? "font-semibold" : "";
    const total = months.reduce((sum, m) => sum + getValue(m), 0);

    return (
        <TableRow>
            <TableCell className="pl-8 text-sm text-muted-foreground whitespace-nowrap md:whitespace-normal md:sticky md:left-0 md:z-10 md:bg-card">
                {label}
            </TableCell>
            {months.map((m, i) => (
                <TableCell
                    key={i}
                    className={`text-right font-mono text-sm ${colorClass} ${weightClass}`}
                >
                    {fmtCents(getValue(m))}
                </TableCell>
            ))}
            <TableCell
                className={`text-right font-mono text-sm font-bold border-l ${colorClass}`}
            >
                {fmtCents(total)}
            </TableCell>
        </TableRow>
    );
}

function SeparatorRow({ colSpan }: { colSpan: number }) {
    return (
        <TableRow>
            <TableCell colSpan={colSpan} className="py-0.5" />
        </TableRow>
    );
}

interface MonthlyTableShellProps {
    monthLabels: { key: string; label: string; claimCount: number }[];
    children: ReactNode;
    onMonthClick?: (month: string) => void;
}

function MonthlyTableShell({ monthLabels, children, onMonthClick }: MonthlyTableShellProps) {
    return (
        <div className="rounded-md border bg-card text-card-foreground overflow-x-auto">
            <Table className="min-w-[760px] table-fixed md:min-w-0 md:table-auto">
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-[200px] min-w-[200px] md:w-auto md:sticky md:left-0 md:z-10 md:bg-card" />
                        {monthLabels.map((m) => (
                            <TableHead key={m.key} className="w-[110px] min-w-[110px] text-right align-top md:w-auto md:align-middle">
                                <div className="flex items-center justify-end gap-1 whitespace-nowrap md:whitespace-normal">
                                    {m.label}
                                    {onMonthClick && (
                                        <button
                                            onClick={() => onMonthClick(m.key)}
                                            className="text-muted-foreground hover:text-primary transition-colors"
                                            aria-label={`View claims for ${m.label}`}
                                            title={`View claims for ${m.label}`}
                                        >
                                            <ExternalLink className="h-3 w-3" />
                                        </button>
                                    )}
                                </div>
                                <div className="whitespace-nowrap text-xs font-normal text-muted-foreground max-md:mt-0.5 md:whitespace-normal">
                                    {m.claimCount} {m.claimCount === 1 ? "claim" : "claims"}
                                </div>
                            </TableHead>
                        ))}
                        <TableHead className="w-[120px] min-w-[120px] text-right border-l font-bold md:w-auto">
                            Total
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>{children}</TableBody>
            </Table>
        </div>
    );
}

function EmptyState() {
    return (
        <div className="text-center py-12 text-muted-foreground border rounded-md bg-card">
            No claims found. Create a claim to see monthly reports.
        </div>
    );
}

// ── Section Components (one per tab) ─────────────────

export function ClaimReceivablesSection({ data, onMonthClick }: { data: MonthlyReceivables[]; onMonthClick?: (month: string) => void }) {
    if (data.length === 0) return <EmptyState />;
    const colSpan = data.length + 2;
    const monthLabels = data.map((m) => ({ key: m.month, label: m.label, claimCount: m.claimCount }));

    return (
        <MonthlyTableShell monthLabels={monthLabels} onMonthClick={onMonthClick}>
            <DataRow label="RCV" months={data} getValue={(m) => m.rcvCents} />
            <DataRow label="Depreciation" months={data} getValue={(m) => m.depreciationCents} color="red" />
            <SeparatorRow colSpan={colSpan} />
            <DataRow label="ACV" months={data} getValue={(m) => m.acvCents} bold />
            <DataRow label="Deductible" months={data} getValue={(m) => m.deductibleCents} color="orange" />
            <SeparatorRow colSpan={colSpan} />
            <DataRow label="Net Claim" months={data} getValue={(m) => m.netClaimCents} bold />
            <DataRow label="Recoverable Depr." months={data} getValue={(m) => m.recoverableDepreciationCents} color="green" />
            <DataRow label="Non-Recoverable Depr." months={data} getValue={(m) => m.nonRecoverableDepreciationCents} color="red" />
            <SeparatorRow colSpan={colSpan} />
            <DataRow label="Net w/ Recoverable" months={data} getValue={(m) => m.netWithRecoverableCents} bold color="green" />
        </MonthlyTableShell>
    );
}

export function PaymentsSection({ data, onMonthClick }: { data: MonthlyPayments[]; onMonthClick?: (month: string) => void }) {
    if (data.length === 0) return <EmptyState />;
    const colSpan = data.length + 2;
    const monthLabels = data.map((m) => ({ key: m.month, label: m.label, claimCount: m.claimCount }));

    return (
        <MonthlyTableShell monthLabels={monthLabels} onMonthClick={onMonthClick}>
            <DataRow label="Carrier Payment" months={data} getValue={(m) => m.carrierPaymentCents} />
            <DataRow label="Supplement Payment" months={data} getValue={(m) => m.supplementPaymentCents} />
            <DataRow label="Depreciation Release" months={data} getValue={(m) => m.depreciationReleaseCents} />
            <DataRow label="Mortgage Release" months={data} getValue={(m) => m.mortgageReleaseCents} />
            <DataRow label="Other" months={data} getValue={(m) => m.otherPaymentCents} />
            <SeparatorRow colSpan={colSpan} />
            <DataRow label="Total Payments" months={data} getValue={(m) => m.totalPaymentsCents} bold color="emerald" />
        </MonthlyTableShell>
    );
}

export function RevenueAccountsReceivableSection({ data, onMonthClick }: { data: MonthlyRevenue[]; onMonthClick?: (month: string) => void }) {
    if (data.length === 0) return <EmptyState />;
    const colSpan = data.length + 2;
    const monthLabels = data.map((m) => ({ key: m.month, label: m.label, claimCount: m.claimCount }));

    return (
        <MonthlyTableShell monthLabels={monthLabels} onMonthClick={onMonthClick}>
            {/* ── Expected Revenue ── */}
            <TableRow>
                <TableCell colSpan={colSpan} className="font-semibold text-xs uppercase tracking-wider text-muted-foreground bg-muted/50 py-1.5 pl-4">
                    Expected Revenue
                </TableCell>
            </TableRow>
            <DataRow label="Flat" months={data} getValue={(m) => m.expectedFlatCents} />
            <DataRow label="Percentage" months={data} getValue={(m) => m.expectedPercentageCents} />
            <SeparatorRow colSpan={colSpan} />
            <DataRow label="Total Expected" months={data} getValue={(m) => m.expectedTotalCents} bold />

            <SeparatorRow colSpan={colSpan} />

            {/* ── Actual Revenue ── */}
            <TableRow>
                <TableCell colSpan={colSpan} className="font-semibold text-xs uppercase tracking-wider text-muted-foreground bg-muted/50 py-1.5 pl-4">
                    Actual Revenue
                </TableCell>
            </TableRow>
            <DataRow label="Flat" months={data} getValue={(m) => m.actualFlatCents} color="emerald" />
            <DataRow label="Percentage" months={data} getValue={(m) => m.actualPercentageCents} color="emerald" />
            <SeparatorRow colSpan={colSpan} />
            <DataRow label="Total Actual" months={data} getValue={(m) => m.actualTotalCents} bold color="emerald" />

            <SeparatorRow colSpan={colSpan} />

            {/* ── Accounts Receivable ── */}
            <TableRow>
                <TableCell colSpan={colSpan} className="font-semibold text-xs uppercase tracking-wider text-muted-foreground bg-muted/50 py-1.5 pl-4">
                    Accounts Receivable
                </TableCell>
            </TableRow>
            <DataRow label="Flat" months={data} getValue={(m) => m.outstandingFlatCents} color="orange" />
            <DataRow label="Percentage" months={data} getValue={(m) => m.outstandingPercentageCents} color="orange" />
            <SeparatorRow colSpan={colSpan} />
            <DataRow label="Total A/R" months={data} getValue={(m) => m.outstandingTotalCents} bold color="orange" />
        </MonthlyTableShell>
    );
}
